
import { BehaviorSubject, Subject } from 'rxjs';
import { InstructionGroupData } from '../components/me-tips/me-tips.service';
import { MeMaxValidation } from '../components/me-validation/me-max-validation';
import { MeRequiredValidation } from '../components/me-validation/me-required-validation';
import { MeValidInputOnlyValidation } from '../components/me-validation/me-valid-input-only-validation';
import { MeValidation } from '../components/me-validation/me-validation';
import { DisclaimersAllLang } from '../services/external-settings/external-settings.interface';
import { ActivityState } from './me-activity.service';
import { Country } from '../interfaces/country';
import { PayeeTypes } from 'src/app/business/manage-payees/manage-payees.bean';
import { MEDocumentUploaderBrick } from '../components/me-form-group/me-document-uploader/me-document-uploader.component';

export interface CountryGroup {
  letter: string;
  countryGroup: Country[];
}

export interface AccountType {
  label: string;
  value: string;
}

type ValidationType =
  | 'meRequiredValidation'
  | 'meValidInputOnlyValidation'
  | 'meMaxValidation';


export interface MeValidationTypes {
  meRequiredValidation?: MeRequiredValidation;
  meValidInputOnlyValidation?: MeValidInputOnlyValidation;
  meMaxValidation?: MeMaxValidation;
}

export enum MeBrickSpanType {
  Span_25 = 'Span_25',
  Span_50 = 'Span_50',
  Span_75 = 'Span_75',
  Span_100 = 'Span_100',
}

export interface MeBrickInputUISetupByScreen {
  span: MeBrickSpanType,
}

export interface MEBrickInputUI {
  mobile: MeBrickInputUISetupByScreen,
  tablet: MeBrickInputUISetupByScreen,
  desktop: MeBrickInputUISetupByScreen
}

export interface MEBrickReviewUI {
  isTextBold: boolean;
}

export class MEBrickUIConfig {

  public inputUI!: MEBrickInputUI;
  public reviewUI!: MEBrickReviewUI;
  public summaryUI!: MEBrickReviewUI;

  constructor() {
    this.initDefaultBrickUIConfig();
  }

  initDefaultBrickUIConfig() {
    this.inputUI = {
      mobile: {
        span: MeBrickSpanType.Span_100,
      },
      tablet: {
        span: MeBrickSpanType.Span_50,
      },
      desktop: {
        span: MeBrickSpanType.Span_50,
      }
    };
    this.reviewUI = {
      isTextBold: false,
    };
    this.summaryUI = {
      isTextBold: false,
    };
  }

  updateInputUIMobile(inputUISetup: MeBrickInputUISetupByScreen) {
    this.inputUI.mobile = inputUISetup;
  }
  updateInputUITablet(inputUISetup: MeBrickInputUISetupByScreen) {
    this.inputUI.tablet = inputUISetup;
  }
  updateInputUIDesktop(inputUISetup: MeBrickInputUISetupByScreen) {
    this.inputUI.desktop = inputUISetup;
  }

  updateReviewUI(reviewUI: MEBrickReviewUI) {
    this.reviewUI = reviewUI;
  }
  updateSummaryUI(summaryUI: MEBrickReviewUI) {
    this.summaryUI = summaryUI;
  }
}

export enum MEBrickAppearance {
  FILL = 'fill',
  OUTLINE = 'outline'
}

export abstract class MEBrick {

  static readonly INPUT: string = "INPUT";
  static readonly INPUT_TOPUP_PHONE_NUMBER: string = "INPUT_TOPUP_PHONE_NUMBER";
  static readonly INPUT_VIZ_PAYEE_PHONE_NUMBER: string = "INPUT_VIZ_PAYEE_PHONE_NUMBER";
  static readonly INPUT_NUMBER: string = "INPUT_NUMBER";
  static readonly NUMBER: string = "NUMBER";
  static readonly INPUT_WITH_BUTTON: string = "INPUT_WITH_BUTTON";
  static readonly BUTTON_SSB: string = "ACTION_BUTTON_SSB";
  static readonly SELECT: string = "SELECT";
  static readonly SELECT_DENSE: string = "SELECT_DENSE";
  static readonly COUNTRY_PICKER: string = "COUNTRY_PICKER";
  static readonly CHECKBOX: string = "CHECKBOX";
  static readonly CONTROL_SELECT: string = "CONTROL_SELECT";
  static readonly CONTROL_SLIDE_TOGGLE: string = "CONTROL_SLIDE_TOGGLE";
  static readonly RADIO_BUTTON: string = "RADIO_BUTTON";
  static readonly RADIO_BUTTON_GROUP: string = "RADIO_BUTTON_GROUP";
  static readonly DOCUMENT_UPLOADER: string = "DOCUMENT_UPLOADER";
  static readonly DATE_PICKER: string = "DATE_PICKER";
  static readonly TEXT_AREA: string = "TEXT_AREA";
  static readonly DYNAMIC_SELECT: string = "DYNAMIC_SELECT";
  static readonly DYNAMIC_SELECT_PBT_FROM_ACCOUNT: string = "DYNAMIC_SELECT_PBT_FROM_ACCOUNT";
  static readonly DYNAMIC_TEXT: string = "DYNAMIC_TEXT";
  static readonly ACCOUNT_SELECTOR_FROM: string = "ACCOUNT_SELECTOR_FROM";
  static readonly ACCOUNT_SELECTOR_TO: string = "ACCOUNT_SELECTOR_TO";
  static readonly CONTROL_CHECKBOX: string = "CONTROL_CHECKBOX";
  static readonly DISCLAIMER: string = "DISCLAIMER";
  static readonly ORDER_DISCLAIMER: string = "ORDER_DISCLAIMER";
  static readonly DECLARATION: string = "DECLARATION";
  static readonly CASH_ORDER: string = "CASH_ORDER";
  static readonly PENDING_BANNER: string = "PENDING_BANNER";
  static readonly INSTRUCTIONS: string = "INSTRUCTIONS";
  static readonly SPACER: string = "SPACER";
  static readonly PAYMENT_READONLY_ITEM: string = "PAYMENT_READONLY_ITEM"
  static readonly PAYEE_SELECTOR: string = "PAYEE_SELECTOR";
  static readonly VIZ_PAYEE_SELECTOR: string = "VIZ_PAYEE_SELECTOR";
  static readonly VIZ_CARD_SELECTOR: string = "VIZ_CARD_SELECTOR";
  static readonly BUTTON: string = "BUTTON";
  static readonly AUTOCOMPLETE_SELECT: string = "AUTOCOMPLETE_SELECT";
  static readonly DYNAMIC_INFO: string = "DYNAMIC_INFO";
  static readonly PHOTO_SELECTOR: string = "PHOTO_SELECTOR";

  public isTranslationRequired: boolean = false;
  public visible$: BehaviorSubject<boolean>;

  constructor(public name: string,
    public index: number,
    public type: string,
    public label: string,
    public value: any,
    public validation: MeValidation[] = MEBrick.NO_VALIDATION,
    public tipsMessage: string[] = MEBrick.NO_TIPS_MESSAGE,
    public span: boolean = MEBrick.NO_SPAN_ENTIRE_ROW,
    public getReviewDisplay = MEBrick.DEFAULT_REVIEW_DISPLAY,
    public reviewIndex: number = MEBrick.NOT_SHOW_IN_REVIEW,
    public summaryIndex: number = MEBrick.NOT_SHOW_IN_SUMMARY,
    public instructions: string[] = MEBrick.NO_INSTRUCTIONS,
    public disclaimers: DisclaimersAllLang | string = MEBrick.NO_DISCLAIMERS,
    public isFormBrick: boolean = MEBrick.IS_FORM_BRICK,
    public isDisabled: boolean = MEBrick.CONTROL_ENABLED,
    public compressed: boolean = MEBrick.NO_COMPRESS_INTO_COLUMN,
    visible$: boolean = MEBrick.DEFAULT_VISIBLE,
    public getSummaryDisplay = MEBrick.DEFAULT_SUMMARY_DISPLAY,
    public uiConfig: MEBrickUIConfig = new MEBrickUIConfig(),
    public compareFn: (o1: any, o2: any)=>boolean = MEBrick.DEFAULT_COMPARE_FN,
    public appearance: MEBrickAppearance = MEBrickAppearance.OUTLINE,
  ) {
    this.visible$ = new BehaviorSubject(visible$);
  }

  public abstract getValue(): any;

  public static readonly REQUIRED: boolean = true;
  public static readonly NOT_REQUIRED: boolean = false;

  public static readonly NO_SPAN_ENTIRE_ROW: boolean = false;
  public static readonly NO_COMPRESS_INTO_COLUMN: boolean = false;
  public static readonly SPAN_ENTIRE_ROW: boolean = true;
  public static readonly COMPRESS_INTO_COLUMN: true;

  public static readonly NO_TIPS_MESSAGE: string[] = [];
  public static readonly NO_VALIDATION: MeValidation[] = [];
  public static readonly NOT_SHOW_IN_REVIEW = -1;
  public static readonly NOT_SHOW_IN_SUMMARY = -1;
  public static readonly NO_INSTRUCTIONS = [];
  public static readonly NO_DISCLAIMERS: DisclaimersAllLang | string = '';
  public static readonly IS_FORM_BRICK: boolean = true;
  public static readonly CONTROL_DISABLED: boolean = true;
  public static readonly CONTROL_ENABLED: boolean = false;
  public static readonly NOT_VISIBLE:  boolean = false;
  public static readonly VISIBLE: boolean = true;
  public static readonly DEFAULT_VISIBLE = MEBrick.VISIBLE;
  public static readonly DEFAULT_APPEARANCE = MEBrick.VISIBLE;

  /**
   * This Method is only used to reuse the Review Display for Pending Order / Order History
   */
  public setReviewValue(value: any) {
    this.value = (value === '' || value === null || value === undefined) ? "N/A" : value;
    // this.value = (value ?? "N/A"); // this case cannot exclude the case of empty string
    this.reviewIndex = 1;

    // Override the original review display if necessary
    switch (this.type) {
      case MEBrick.CHECKBOX:
        // Default already Yes/No Display
        // this.getReviewDisplay = MEBrick.DEFAULT_YES_OR_NO_DISPLAY;
        break;
      case MEBrick.DECLARATION:
        this.getReviewDisplay = MEBrick.DEFAULT_YES_OR_NO_DISPLAY;
        break;
      default:
        this.getReviewDisplay = MEBrick.DEFAULT_REVIEW_DISPLAY;
        break;
    }
  }

  public hideReview() {
    // Debug Mode to show the name and value
    // this.getReviewDisplay = (value: any) => {
    //   return this.name + " : " + value;
    // }

    this.reviewIndex = -1;
  }

  public static DEFAULT_YES_OR_NO_DISPLAY(value: boolean): string {
    return (value) ? "LABEL_YES" : "LABEL_NO";
  }
 
  public static DEFAULT_REVIEW_DISPLAY(value: any): string {
    return value;
  }

  public static DEFAULT_SUMMARY_DISPLAY(value: any): string {
    return value;
  }

  public static REVIEW_DISPLAY_NA(value: any): string {
    return value ?? "N/A";
  }

  public static DEFAULT_COMPARE_FN(o1: any, o2: any): boolean {
    return o1 && o2 && o1 == o2;
  }

  public removeValidation(validation : MeValidation) {
    this.removeValidationByType(validation.validationType);
  }

  public replaceValidation(validation: MeValidation) { 
    this.removeValidation(validation);
    this.addValidation(validation);
  }

  public addValidation(validation: MeValidation) { 
    this.validation.push(validation);
  }

  public removeValidationByType(validationType : string) {
    this.validation = this.validation.filter((v: MeValidation) => { return v.validationType != validationType });
  }

  public replaceGetReviewDisplay(value: (value: any)=> string) {
    this.getReviewDisplay = value;
  }

  public replaceGetSummaryDisplay(value: (value: any)=> string) {
    this.getSummaryDisplay = value;
  }

}

export class MEGroup {

  static readonly DEFAULT_VISIBLE = true;
  static readonly DEFAULT_INVISIBLE = false;

  public grpVisible$: Subject<boolean>

  constructor(
    public name: string,
    public index: number,
    public label?: string,
    public labelOptionA?: string,
    public bricks: MEBrick[] = [],
    public tipsMessage?: string[],
    public layout?: string,
    private grpVisible: boolean = MEGroup.DEFAULT_VISIBLE,
    public getGrpReviewDisplay: (value: any) => string = MEGroup.DEFAULT_GROUP_REVIEW_DISPLAY,
    public hasValue: (bricks: MEBrick[]) => boolean = MEGroup.DEFAULT_GROUP_HAS_VALUE) {

    // Use Subject in order to skip Initial Triggering
    this.grpVisible$ = new Subject(); 
    this.grpVisible$.subscribe((isVisible) => {
      this.grpVisible = isVisible;
      this.bricks.forEach((brick, index) => {
        brick.visible$.next(isVisible);
      })
    })

    if (!grpVisible) {
      this.grpVisible$.next(false)
    }
  }

  public isVisible() {
    return this.grpVisible;
  }

  public getReviewBricks(): MEBrick[] {
    return this.bricks.filter((brick: MEBrick, index) => {
      return brick.reviewIndex != -1
    })
  }

  public getSummaryBricks(): MEBrick[] {
    return this.bricks.filter((brick: MEBrick, index) => {
      return brick.summaryIndex != -1
    })
  }

  public findBrickValue(name: string): any {
    let brick: MEBrick = this.findBrick(name);
    return brick?.getValue();
  }

  public findBrick(name: string): any {
    let brick: MEBrick | undefined = this.bricks.find(
      (brick) => brick.name === name
    );

    return brick;
  }

  public removeBrick(name: string) {
    if (this.bricks) {
      this.bricks = this.bricks.filter((b: MEBrick) => { return b.name != name });
    }
  }

  public replaceBrick(brick: MEBrick){
    if (this.bricks) {
      let name = brick.name;
      let index = this.bricks.findIndex((b: MEBrick) => { return b.name == name });
      if(index != -1){
        this.bricks[index] = brick;
      }
    }
  }

  public addBrick(brick: MEBrick, index: number = -1) {
    if (index == -1) {
      index = this.bricks.length; // add to the end
    }
    this.bricks.splice(index, 0, brick);
    this.reOrderBricks();
  }

  public addBricks(newBricks: MEBrick[], anchorBrick: MEBrick | null = null) {
    let index = this.bricks.findIndex(item => item == anchorBrick);
    if (index == -1) {
      index = this.bricks.length;
    }
    this.bricks = [...this.bricks.slice(0, index), ...newBricks, ...this.bricks.slice(index)]
    this.reOrderBricks();
  }

  public reOrderBricks() {
    for (let i = 0; i < this.bricks.length; i++) {
      this.bricks[i].index = (i + 1);
    }
  }

  /**
   * This Method is only used to reuse the Review Display for Pending Order / Order History
   * Hide all the Bricks and the use MEBrick.setReviewValue to reset it
   */
  public hideAllReviewBricks() {
    for (let i = 0; i < this.bricks.length; i++) {
      this.bricks[i].hideReview()
    }
  }

  /**
   * Update Field Label is to update the display field due to various bank customization
   */
  public updateFieldLabel(name: string, label: string) {
    let brick = this.findBrick(name);
    if (brick) {
      brick.label = label;
    }
  }

  /**
   * Update Field Placeholder is to update the placeholder due to various bank customization
   */
  public updatePlaceholder(name: string, placeholder: string) {
    let brick = this.findBrick(name);
    if (brick) {
      brick.placeholder = placeholder;
    }
  }

  public static DEFAULT_GROUP_REVIEW_DISPLAY(value: any): string {
    return value;
  }
  
  public static DEFAULT_GROUP_HAS_VALUE(bricks: MEBrick[]): boolean {
    return bricks.some((brick: MEBrick) => MEGroup.brickHasValue(brick));
  }

  public static DEFAULT_GROUP_HAS_VALUE_BOOLEAN(bricks: MEBrick[]): boolean {
    let brick = bricks.find((brick: MEBrick) => [MEBrick.CONTROL_CHECKBOX, MEBrick.CONTROL_SLIDE_TOGGLE, MEBrick.CHECKBOX].includes(brick.type));
    return brick?.value;
  }

  public static DEFAULT_GROUP_HAS_VALUE_UI(bricks: MEBrick[]): boolean {
    let brick = bricks.find((brick: MEBrick) => [MEBrick.DISCLAIMER, MEBrick.ORDER_DISCLAIMER].includes(brick.type));
    return brick === undefined;
  }

  public static DEFAULT_GROUP_HAS_VALUE_DOCUMENT(bricks: MEBrick[]): boolean {
    let brick = bricks.find((brick: MEBrick) => [MEBrick.DOCUMENT_UPLOADER].includes(brick.type));
    return (brick as MEDocumentUploaderBrick).files.length > 0;
  }
  
  private static brickHasValue(brick: MEBrick): boolean {
    // Adjust based on your brick structure
    if (brick.value === null || brick.value === undefined || brick.value === '' || brick.value === false) return false;
    if (Array.isArray(brick.value) && brick.value.length === 0) return false;
    return true;
  }
}

export function findBrickValue(group: MEGroup, name: string): any {
  if (group) {
    return group.findBrickValue(name);
  } else {
    return null;
  }
}

export function findBrick(group: MEGroup, name: string): any {
  if (group) {
    return group.findBrick(name);
  } else {
    return null;
  }
}

/**
export function findBrickValue(group: MEGroup, name: string): any {
  let brick: MEBrick | undefined = group.bricks?.find(
    (brick) => brick.name === name
  );

  return brick?.getValue();
}
*/


export class MEGroupBuilder {

  private label?: string;
  private labelOptionA?: string;
  private bricks: MEBrick[] = [];
  private tipsMessage?: string[];
  private layout?: string;
  private visible = MEGroup.DEFAULT_VISIBLE;
  private grpReviewDisplay: (value: any) => string = MEGroup.DEFAULT_GROUP_REVIEW_DISPLAY;
  private hasValue: (bricks: MEBrick[]) => boolean = MEGroup.DEFAULT_GROUP_HAS_VALUE;


  constructor(private name: string, private index: number) { }

  setLabel(label: string) {
    this.label = label;
    return this;
  }
  setLabelOption(labelOptionA: string) {
    this.labelOptionA = labelOptionA;
    return this;
  }
  setBricks(bricks: MEBrick[]) {
    this.bricks = bricks;
    return this;
  }
  setTipMessage(tipsMessage: string[]) {
    this.tipsMessage = tipsMessage;
    return this;
  }
  setLayout(layout: string) {
    this.layout = layout;
    return this;
  }
  setVisible(visible: boolean) {
    this.visible = visible;
    return this;
  }
  setGrpReviewDisplay(grpReviewDisplay: any) {
    this.grpReviewDisplay = grpReviewDisplay;
    return this;
  }
  setHasValue(hasValue: any) {
    this.hasValue = hasValue;
    return this;
  }

  build(): MEGroup {
    return new MEGroup(this.name, this.index, this.label, this.labelOptionA, this.bricks, this.tipsMessage, this.layout, this.visible, this.grpReviewDisplay, this.hasValue);
  }
}

export function noReviewDisplay(): null {
  return null;
}

export interface MEMetaRoot {
  name: string;
  inputMeta?: {
    name: string;
    layout: string;
    cancelReConfirm?: boolean;
    accessories?: {
      withHeader: boolean;
      withFooter: boolean;
      hideCancel?: boolean;
      cancelButton?: string;
      hideReview?: boolean;
      reviewButton?: string;
    }
  };
  confirmMeta?: {
    name: string;
    layout: string;
    accessories?: {
      withHeader: boolean;
      withFooter: boolean;
      hideBack?: boolean;
      backButton?: string;
      hideSubmit?: boolean;
      submitButton?: string;
    }
  };
  summaryMeta?: {
    name: string;
    layout: string;
    accessories?: {
      withHeader: boolean;
      withFooter: boolean;
      hideFinish?: boolean;
      finishButton?: string;
    }
  };
}

export class MEMeta {
  private root!: MEMetaRoot;
  private fields?: Record<string, MEGroup>;
  private inputSubmissionResult: Record<string, any> = {};
  private orderedFields: OrderedField[] = [];
  private initAccesories: AccessoryContentType = {
    staticContent: [],
    dynamicContent: [],
  }
  private headers: ActivityHeaders = {
    [ActivityState.Input]: { ...this.initAccesories },
    [ActivityState.Review]: { ...this.initAccesories },
    [ActivityState.Summary]: { ...this.initAccesories }
  };
  private footers: ActivityFooters = {
    [ActivityState.Input]: { ...this.initAccesories },
    [ActivityState.Review]: { ...this.initAccesories },
    [ActivityState.Summary]: { ...this.initAccesories }
  };
  private titles: ActivityTitles = {
    [ActivityState.Input]: new ActivityTitle(''),
    [ActivityState.Review]: new ActivityTitle(''),
    [ActivityState.Summary]: new ActivityTitle('')
  };

  setRoot(root: MEMetaRoot) {
    this.root = root;
  }

  getRoot() {
    return this.root;
  }

  setFields(fields: Record<string, MEGroup>) {
    this.fields = fields;
    if (this.fields) {
      this.orderedFields = [];
      for (const key in this.fields) {
        this.orderedFields.push({ name: key, group: this.fields[key] });
      }

      this.orderedFields.sort((f1, f2) => f1.group.index - f2.group.index);
    }
  }

  removeField(grpName: string, fldName: string) {
    if (this.fields) {
      this.fields[grpName].removeBrick(fldName);
    }
  }

  hasField(fldName: string): boolean {
    if (fldName in this.fields!) return true;
    else return false;
  }

  getGroup(name: string): MEGroup | null {
    if (this.fields) return this.fields[name];
    return null;
  }

  getFields(): Record<string, MEGroup> {
    if (this.fields) return this.fields;
    else return {};
  }

  getOrderedFields(): OrderedField[] {
    return this.orderedFields.filter((field) => field.group.isVisible());
  }

  addInputSubmissionResult(key: string, value: any) {
    this.inputSubmissionResult[key] = value;
  }

  getInputSubmissionResult(): Record<string, any> {
    return this.inputSubmissionResult;
  }

  getInputSubmissionData(key: string): any{
    return this.inputSubmissionResult[key];
  }

  setHeader(header: MEBrick[], state: UnionStates, isStatic: boolean) {
    if (isStatic) {
      this.headers[state].staticContent = header;
      if (this.headers[state].staticContent.length > 0) {
        this.headers[state].staticContent.sort((b1, b2) => b1.index - b2.index);
      }
    } else {
      this.headers[state].dynamicContent = header;
      if (this.headers[state].dynamicContent.length > 0) {
        this.headers[state].dynamicContent.sort((b1, b2) => b1.index - b2.index);
      }
    }

  }

  setFooter(footer: MEBrick[], state: UnionStates, isStatic: boolean) {
    if (isStatic) {
      this.footers[state].staticContent = footer;
      if (this.footers[state].staticContent.length > 0) {
        this.footers[state].staticContent.sort((b1, b2) => b1.index - b2.index);
      }
    } else {
      this.footers[state].dynamicContent = footer;
      if (this.footers[state].dynamicContent.length > 0) {
        this.footers[state].dynamicContent.sort((b1, b2) => b1.index - b2.index);
      }
    }

  }

  getHeader(state: UnionStates, isStatic: boolean): MEBrick[] | undefined {
    if (isStatic) {
      return this.headers[state].staticContent ?? undefined;
    } else {
      return this.headers[state].dynamicContent ?? undefined;
    }
  }

  getFooter(state: UnionStates, isStatic: boolean): MEBrick[] | undefined {
    if (isStatic) {
      return this.footers[state].staticContent ?? undefined;
    } else {
      return this.footers[state].dynamicContent ?? undefined;
    }
  }

  setTitles(titles: ActivityTitles) {
    this.titles = titles;
  }

  getTitles(): ActivityTitles {
    return this.titles;
  }

  updateTitle(state: ActivityState, title: ActivityTitle) {
    if (state == ActivityState.None) return;
    this.titles[state] = title;
  }
}

interface OrderedField {
  name: string,
  group: MEGroup
}

export type UnionStates = ActivityState.Input | ActivityState.Review | ActivityState.Summary;

export interface ActivityTitles {
  [ActivityState.Input]: ActivityTitle;
  [ActivityState.Summary]: ActivityTitle;
  [ActivityState.Review]: ActivityTitle;
}

export interface ActivityHeaders {
  [ActivityState.Input]: AccessoryContentType;
  [ActivityState.Summary]: AccessoryContentType;
  [ActivityState.Review]: AccessoryContentType;
}

export interface ActivityFooters {
  [ActivityState.Input]: AccessoryContentType;
  [ActivityState.Summary]: AccessoryContentType;
  [ActivityState.Review]: AccessoryContentType;
}

interface AccessoryContentType {
  staticContent: MEBrick[];
  dynamicContent: MEBrick[];
}

export class ActivityTitle {
  instructionGroupData!: InstructionGroupData;
  mpPayeeType: PayeeTypes = PayeeTypes.TOPUP;
  
  activityTitleType: ActivityTitleTypes = ActivityTitleTypes.Default;

  constructor(public title: string, public rightText: string = '') { }

  setInstructionsData(instructionGroupData: InstructionGroupData) {
    this.instructionGroupData = instructionGroupData;
    return this;
  }

  setManagePayeeRedirectDirection(payeeType: PayeeTypes) {
    this.mpPayeeType = payeeType;
    return this;
  }

  setActivityType(activityTitleType: ActivityTitleTypes = ActivityTitleTypes.Default) {
    this.activityTitleType = activityTitleType;
    return this;
  }
  
  // Handle method to set instruction inline
  setInstruction(title : string, instruction: string, displayCondition: boolean= true, withIndexedInstructions: boolean = false) {
    this.instructionGroupData = {
      title: title,
      instructions: [
        {
          index: 1,
          instruction: instruction,
          displayCondition: displayCondition
        }
      ],
      withIndexedInstructions: withIndexedInstructions
    }
    return this;
  }

  static createTipsData(title: string, instruction: string): ActivityTitle{
    return this.createActivityTipsData("", title, instruction);
  }

  static createActivityTipsData(activityTitle: string, title: string, instruction: string) : ActivityTitle{
    return new ActivityTitle(activityTitle).setInstruction(title, instruction, true, false);
  }  
}

export enum ActivityTitleTypes {
  Default = "DEFAULT",
  WithManagePayees = "WITH_MANAGE_PAYEES"
}

export interface CancelUrl {
  url: string;
  withParams: boolean;
  params: Record<string, any>;
}